import AnomalyModel from "components-for/anomalies/anomaly-model";
import AnomalySummaryCount from "./anomaly-summary-count";
import AnomalySummaryMetricChart from "./anomaly-summary-metric-chart";
import AnomalyTagGroups from "components-for/anomalies/anomaly-tag-groups";
import AnomalyTitle from "./anomaly-title";
import MetricAnomalyDescription from "./metric-anomaly-description";
import Page from "constants/page";
import PhraseCountsChart from "components-for/anomalies/phrase-counts-chart";
import TextAnomalyDescription from "./text-anomaly-description";
import urlJoin from "url-join";
import useAnomalyEvents from "hooks-for/anomalies/use-anomaly-events";
import useModel from "hooks-for/models/use-model";
import { addHours, endOfHour, startOfHour, subHours } from "date-fns";
import { Box, BoxContent } from "@edgetier/client-components";
import { CHART_HOURS_AFTER_ANOMALY, CHART_HOURS_BEFORE_ANOMALY } from "./anomaly-summary.constants";
import { FormattedDateTime } from "@edgetier/client-components";
import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./anomaly-summary.types";
import { Link } from "react-router-dom";
import { ModelType, TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { SpinnerUntil } from "@edgetier/components";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import "./anomaly-summary.scss";

/**
 * Show details of an anomaly that occurred or is ongoing. Some of the details shown depend on whether it's a metric or
 * text anomaly.
 * @returns Anomaly title, description, and chart.
 */
const AnomalySummary: FunctionComponent<IProps> = ({ anomaly }) => {
    const options = { retry: false };
    const { data: anomalyEvents, isLoading: anomalyEventsAreLoading } = useAnomalyEvents(anomaly.anomalyId, options);
    const { data: model, isLoading: modelIsLoading } = useModel(anomaly.modelId, options);

    // An anomaly could be ongoing, so the end date time to use is just now. Need to memo this though so other
    // components aren't loaded automatically every time the component renders.
    const anomalyEndDateTime = useMemo(() => anomaly.endDateTime ?? new Date(), [anomaly.endDateTime]);

    const { data: interactionsCount, isLoading: isInteractionsCountLoading } = useInteractionsCount({
        anomalyId: anomaly.anomalyId,
        endDateTime: anomalyEndDateTime,
        startDateTime: anomaly.startDateTime,
    });

    // Calculate the range of the chart. It will show the occurences of the relevant phrases several days before and
    // after the start of the anomaly.
    const [chartStartDateTime, chartEndDateTime] = useMemo(
        () => [
            startOfHour(subHours(anomaly.startDateTime, CHART_HOURS_BEFORE_ANOMALY)),
            endOfHour(addHours(anomaly.startDateTime, CHART_HOURS_AFTER_ANOMALY)),
        ],
        [anomaly.startDateTime]
    );

    return (
        <Box className="anomaly-summary">
            <BoxContent>
                <SpinnerUntil
                    data={anomalyEvents}
                    isReady={!anomalyEventsAreLoading && !modelIsLoading && !isInteractionsCountLoading}
                >
                    {!Array.isArray(anomalyEvents) || !model || !(typeof interactionsCount === "number") ? (
                        "Error"
                    ) : (
                        <Link to={urlJoin(Page.Anomaly, anomaly.anomalyId)}>
                            <div className="anomaly-summary__details">
                                <div className="anomaly-summary__titles">
                                    <AnomalyTitle displayName={anomaly.displayName} />
                                    <div className="anomaly-summary__dates">
                                        <FormattedDateTime dateTime={anomaly.detectDateTime} />
                                    </div>

                                    {model.model && <AnomalyModel model={model.model} size="small" />}
                                    {model.tags.length > 0 && <AnomalyTagGroups size="small" tags={model.modelTags} />}
                                </div>

                                <div className="anomaly-summary__description">
                                    {model.modelType === ModelType.Metric && (
                                        <MetricAnomalyDescription anomalyEvents={anomalyEvents} />
                                    )}
                                    {model.modelType === ModelType.Text && (
                                        <TextAnomalyDescription anomalyEvents={anomalyEvents} />
                                    )}
                                </div>
                            </div>
                            <div className="anomaly-summary__chart">
                                {model.modelType === ModelType.Metric && (
                                    <AnomalySummaryMetricChart
                                        anomalyEvents={anomalyEvents}
                                        endDateTime={anomaly.endDateTime}
                                        modelId={model.modelId}
                                        startDateTime={anomaly.startDateTime}
                                    />
                                )}

                                {model.modelType === ModelType.Text && (
                                    <PhraseCountsChart
                                        anomalyId={anomaly.anomalyId}
                                        endDateTime={chartEndDateTime}
                                        groupBy={TimePeriodGroupBy.Days}
                                        highlightStartDateTime={anomaly.startDateTime}
                                        highlightEndDateTime={anomaly.endDateTime}
                                        isPreview={true}
                                        mouseCallbacks={{}}
                                        startDateTime={chartStartDateTime}
                                        mandatoryParameters={{
                                            startDateTime: chartStartDateTime,
                                            endDateTime: chartEndDateTime,
                                        }}
                                    />
                                )}
                            </div>

                            <AnomalySummaryCount interactionsCount={interactionsCount} />
                        </Link>
                    )}
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(AnomalySummary);
